import { Children, forwardRef, isValidElement, memo, useEffect } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { cx } from '@/utils/cx';

import { useIsDesktop } from '../HeaderContext';

import styles from './HeaderRight.module.scss';

import type { VisibleOn } from '../types';

export interface HeaderRightProps extends ComponentPropsWithoutRef<'div'> {
  /** 특정 디바이스에서만 노출. 미지정 = 항상 노출 */
  visibleOn?: VisibleOn;
}

const MAX_DESKTOP = 4;
const MAX_MOBILE = 3;

function countByDevice(children: HeaderRightProps['children']) {
  let desktopCount = 0;
  let mobileCount = 0;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const childVisibleOn = (child.props as { visibleOn?: string }).visibleOn;

    if (childVisibleOn !== 'mobile') desktopCount++;
    if (childVisibleOn !== 'desktop') mobileCount++;
  });

  return { desktopCount, mobileCount };
}

export const HeaderRight = memo(
  forwardRef<HTMLDivElement, HeaderRightProps>(({ children, className, visibleOn, ...rest }, ref) => {
    const isDesktop = useIsDesktop();

    useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;

      const { desktopCount, mobileCount } = countByDevice(children);

      if (isDesktop && desktopCount > MAX_DESKTOP) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Header.Right] 데스크탑에서 ${desktopCount}개 노출됨 (최대 ${MAX_DESKTOP}개 권장). visibleOn="desktop"으로 디바이스별 노출을 제어하세요.`,
        );
      }
      if (!isDesktop && mobileCount > MAX_MOBILE) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Header.Right] 모바일에서 ${mobileCount}개 노출됨 (최대 ${MAX_MOBILE}개 권장). visibleOn="desktop"으로 데스크탑 전용 항목을 지정하세요.`,
        );
      }
    }, [isDesktop, children]);

    return (
      <div
        className={cx(styles.right, className)}
        data-core-ui-header-slot="right"
        data-core-ui-visible-on={visibleOn}
        ref={ref}
        {...rest}
      >
        {children}
      </div>
    );
  }),
);

HeaderRight.displayName = 'Header.Right';
